import React, { useRef } from 'react';
import Modal from 'react-modal';
import { 
    FiX, FiFileText, FiUser, FiCalendar, FiFlag, 
    FiBriefcase, FiFolder, FiSettings, FiCheckCircle, 
    FiClock, FiClipboard, FiCheck 
} from 'react-icons/fi';

// Set up the modal root element for accessibility
if (typeof window !== 'undefined' && document.getElementById('root')) {
    Modal.setAppElement('#root');
}

/**
 * ViewRequisitionContent - Pure UI component for displaying requisition details
 */
export const ViewRequisitionContent = ({ 
    requisition, 
    onRequestClose, 
    handleOpenCommentsModal,
    EXECUTIVE_APPROVAL_THRESHOLD 
}) => {
    if (!requisition) return null;

    const getStatusColor = (status) => {
        const s = status?.toLowerCase() || '';
        if (s.includes('approved') || s.includes('completed')) return 'bg-emerald-50 text-emerald-600 border-emerald-100';
        if (s.includes('rejected')) return 'bg-rose-50 text-rose-600 border-rose-100';
        if (s.includes('pending') || s.includes('submitted')) return 'bg-amber-50 text-amber-600 border-amber-100';
        return 'bg-gray-50 text-gray-600 border-gray-100';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            maximumFractionDigits: 0
        }).format(amount || 0).replace('UGX', 'SSP');
    };

    const PROCUREMENT_LABELS = {
        'goods_services': 'Goods & Services',
        'financial_request': 'Financial Request',
        'travel': 'Travel Request',
        'contract': 'Contract Request',
        'inquiry_resolution': 'Inquiry / Resolution'
    };

    const requester = requisition.requisitionBy || requisition.requester || {};
    const isAwaitingMyAction = ['submitted', 'pending_director_approval'].includes(requisition.currentStatus);
    const isOverThreshold = (requisition.totalAmount || requisition.estimatedCost) > EXECUTIVE_APPROVAL_THRESHOLD;

    return (
        <div className="bg-gray-50/50 relative min-h-full">
            {/* Premium Orange Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-10 py-10 shadow-lg flex justify-between items-start text-white">
                <div>
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-white/20 border border-white/30 text-white shadow-sm">
                            {requisition.currentStatus?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-[10px] font-black text-orange-100 uppercase tracking-widest opacity-80 bg-orange-700/20 px-3 py-1.5 rounded-full">
                            Ref: {requisition._id?.slice(-8).toUpperCase()}
                        </span>
                    </div>
                    <h2 className="text-4xl font-black text-white tracking-tight leading-tight uppercase max-w-2xl">
                        {requisition.title}
                    </h2>
                </div>
                <button 
                    onClick={onRequestClose}
                    className="size-14 rounded-2xl bg-white/10 flex items-center justify-center text-white hover:bg-white hover:text-orange-600 transition-all duration-500 group active:scale-95 shadow-lg"
                >
                    <FiX size={24} className="group-hover:rotate-90 transition-transform duration-500" />
                </button>
            </div>

            {/* Content Body */}
            <div className="p-10 space-y-10 pb-40">
                
                {/* Meta Overview Row - High Contrast Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="p-8 bg-white rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 hover:border-orange-200 transition-colors">
                        <div className="size-14 rounded-2xl bg-orange-100 flex items-center justify-center text-orange-600 text-lg font-black uppercase">
                            {requester.firstName?.charAt(0)}{requester.lastName?.charAt(0)}
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Requester</p>
                            <p className="text-base font-black text-gray-900 uppercase tracking-tight">{requester.firstName} {requester.lastName}</p>
                        </div>
                    </div>

                    <div className="p-8 bg-white rounded-[2rem] border border-gray-100 shadow-sm hover:border-emerald-200 transition-colors">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Project Info</p>
                        <p className="text-base font-black text-gray-900 uppercase tracking-tight leading-snug">{requisition.projectId?.projectTitle || 'General Overhead'}</p>
                        <p className="text-[11px] font-bold text-emerald-600 uppercase mt-2 inline-block bg-emerald-50 px-3 py-1 rounded-lg">Code: {requisition.projectId?.budgetCode || 'N/A'}</p>
                    </div>

                    <div className="p-8 bg-white rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 hover:border-blue-200 transition-colors">
                        <div className="size-14 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400">
                            <FiClipboard size={24} />
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Procurement Type</p>
                            <p className="text-base font-black text-gray-900 uppercase tracking-tight">{PROCUREMENT_LABELS[requisition.typeOfProcurement] || requisition.typeOfProcurement}</p>
                        </div>
                    </div>
                </div>

                {/* Technical Justification */}
                <div className="bg-white p-10 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-5">
                    <div className="flex items-center gap-4 border-b border-gray-50 pb-5">
                        <FiFileText className="text-orange-500" size={22} strokeWidth={2.5} />
                        <p className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Description & Justification</p>
                    </div>
                    <p className="text-lg font-medium text-gray-700 leading-relaxed italic pr-6 border-l-4 border-orange-100 pl-6">
                        "{requisition.description || 'No description provided.'}"
                    </p>
                </div>

                {/* Items Table Section */}
                {requisition.typeOfProcurement === 'goods_services' && requisition.lineItems && (
                    <div className="bg-white p-10 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-8">
                        <div className="flex items-center justify-between border-b border-gray-50 pb-6">
                            <div className="flex items-center gap-4">
                                <FiBriefcase className="text-orange-500" size={22} strokeWidth={2.5} />
                                <p className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Bill of Quantities ({requisition.lineItems.length} Items)</p>
                            </div>
                            <div className="text-right">
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Valuation</p>
                                <span className="text-2xl font-black text-gray-900">{formatCurrency(requisition.estimatedCost || requisition.totalAmount)}</span>
                            </div>
                        </div>
                        
                        <div className="overflow-x-auto rounded-3xl border border-gray-50">
                            <table className="min-w-full divide-y divide-gray-100">
                                <thead className="bg-gray-50/50 text-left">
                                    <tr>
                                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Description</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Qty</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Unit Price</th>
                                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Subtotal</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-50">
                                    {requisition.lineItems.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-orange-50/30 transition-colors">
                                            <td className="px-8 py-6 text-sm font-black text-gray-800 uppercase tracking-tight">{item.itemName}</td>
                                            <td className="px-8 py-6 text-sm font-bold text-gray-500 text-center">{item.quantity}</td>
                                            <td className="px-8 py-6 text-sm font-bold text-gray-500 text-right">{item.unitPrice?.toLocaleString()}</td>
                                            <td className="px-8 py-6 text-sm font-black text-gray-900 text-right">{(item.quantity * (item.unitPrice || 0))?.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Travel Details Section */}
                {requisition.typeOfProcurement === 'travel' && requisition.travelDetails && (
                    <div className="bg-white p-10 rounded-[2.5rem] border border-gray-100 shadow-sm space-y-8">
                        <div className="flex items-center gap-4 border-b border-gray-50 pb-6">
                            <FiCalendar className="text-orange-500" size={22} strokeWidth={2.5} />
                            <p className="text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">Travel Logistics Summary</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[
                                { label: 'Destination', value: requisition.travelDetails.destination },
                                { label: 'Purpose', value: requisition.travelDetails.purposeOfTravel || requisition.travelDetails.purpose },
                                { label: 'Period', value: `${new Date(requisition.travelDetails.startDate).toLocaleDateString()} - ${new Date(requisition.travelDetails.endDate).toLocaleDateString()}` },
                                { label: 'Accommodation', value: requisition.travelDetails.accommodationType },
                                { label: 'Transport', value: requisition.travelDetails.transportMode },
                                { label: 'Valuation', value: formatCurrency(requisition.travelDetails.estimatedCost), highlight: true }
                            ].map((detail, idx) => (
                                <div key={idx} className="p-6 bg-gray-50/50 rounded-3xl border border-gray-100/50 hover:bg-white hover:border-orange-100 transition-all">
                                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 font-black">{detail.label}</p>
                                    <p className={`text-base font-black uppercase ${detail.highlight ? 'text-emerald-700' : 'text-gray-900'}`}>
                                        {detail.value || 'N/A'}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Status Footer - Replaces Sticky Actions */}
                <div className="bg-white border-t border-gray-100 p-10 rounded-[3rem] shadow-xl border border-gray-100">
                    <div className="flex flex-col sm:flex-row gap-6">
                        {isAwaitingMyAction ? (
                            <>
                                <div className="flex-1 flex flex-col sm:flex-row gap-4">
                                    {!isOverThreshold ? (
                                        <>
                                            <button 
                                                onClick={() => { handleOpenCommentsModal(requisition, 'approve'); onRequestClose(); }}
                                                className="flex-1 h-20 bg-emerald-600 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-xl shadow-emerald-50 hover:bg-emerald-700 hover:scale-[1.02] transform transition-all flex items-center justify-center gap-3 active:scale-95"
                                            >
                                                <FiCheckCircle size={22} strokeWidth={3} /> Approve Requisition
                                            </button>
                                            <button 
                                                onClick={() => { handleOpenCommentsModal(requisition, 'reject'); onRequestClose(); }}
                                                className="flex-1 h-20 bg-rose-600 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-xl shadow-rose-50 hover:bg-rose-700 hover:scale-[1.02] transform transition-all flex items-center justify-center gap-3 active:scale-95"
                                            >
                                                <FiX size={22} strokeWidth={3} /> Reject Request
                                            </button>
                                        </>
                                    ) : (
                                        <button 
                                            onClick={() => { handleOpenCommentsModal(requisition, 'approve'); onRequestClose(); }}
                                            className="flex-1 h-20 bg-blue-600 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-50 hover:bg-blue-700 hover:scale-[1.02] transform transition-all flex items-center justify-center gap-3 active:scale-95"
                                        >
                                            <FiCheckCircle size={22} strokeWidth={3} /> Review & Forward to Executive
                                        </button>
                                    )}
                                </div>
                                <button 
                                    onClick={onRequestClose}
                                    className="px-12 h-20 bg-gray-50 text-gray-400 rounded-3xl font-black text-xs uppercase tracking-widest hover:bg-gray-100 hover:text-gray-800 transition-all border border-gray-100"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button 
                                onClick={onRequestClose}
                                className="w-full h-20 bg-orange-600 text-white rounded-3xl font-black text-base uppercase tracking-[0.2em] shadow-2xl shadow-orange-100 hover:bg-orange-700 hover:scale-[1.01] transform transition-all flex items-center justify-center gap-4"
                            >
                                Close & Return to Dashboard <FiX size={24} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ViewRequisitionModal = ({ 
    isOpen, 
    onRequestClose, 
    requisition, 
    handleOpenCommentsModal, 
    EXECUTIVE_APPROVAL_THRESHOLD 
}) => {
    const modalRef = useRef(null);
    
    // Handle click outside to close
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && modalRef.current && !modalRef.current.contains(event.target)) {
                onRequestClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onRequestClose]);

    if (!isOpen || !requisition) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className="modal-content outline-none"
            overlayClassName="modal-overlay fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
        >
            <div 
                ref={modalRef}
                className="w-full max-w-4xl mx-auto bg-white rounded-[2.5rem] shadow-2xl overflow-y-auto max-h-[90vh] custom-scrollbar border border-gray-100"
            >
                <ViewRequisitionContent 
                    requisition={requisition} 
                    handleOpenCommentsModal={handleOpenCommentsModal}
                    EXECUTIVE_APPROVAL_THRESHOLD={EXECUTIVE_APPROVAL_THRESHOLD}
                    onRequestClose={onRequestClose} 
                />
            </div>
        </Modal>
    );
};

export default ViewRequisitionModal;